import React, { useState, useEffect, useCallback } from 'react';
import {
  RACAccordion,
  RACNotesDrawer,
  makeStyles,
} from '@rentacenter/racstrap';
import { CustomerInformationDetails } from './CustomerInformationDetails';
import { getStoreConfigs } from '../../../api/store';
import { getSelectedStore } from '../../../utils/utils';
import { useParams, useLocation } from 'react-router-dom';
import { AddNote } from './AddNote';
import { SearchNotes } from './SearchNotes';
import { NotesList } from './NotesList';
import {
  useNotes,
  useNotesActions,
} from '../../../context/Notes/NotesProvider';

const useStyles = makeStyles(() => ({
  customerInformationRoot: {
    width: 'calc(100% + 1.5rem)',
    left: '-0.75rem',
    margin: '0 !important',
    position: 'relative',
  },
  accordionSummaryRoot: {
    width: '10px',
    position: 'absolute',
    right: 10,
    top: '-70px',
  },
  accordionSummaryContent: {
    margin: '0 !important',
  },
  drawerContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  notesListWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    minHeight: 0,
  },
}));

interface RouteParams {
  customerId: string;
}

export const CustomerInformation = () => {
  const classes = useStyles();
  const [notesOpen, setNotesOpen] = useState(false);
  const [enableCustomerNotes, setEnableCustomerNotes] = useState('0');
  // Get customerId from URL params or location state
  const { customerId: paramCustomerId } = useParams<RouteParams>();
  const location = useLocation();
  const pathCustomerId = location?.pathname?.match(/\/customer\/(\d+)/)?.[1];
  const customerId =
    paramCustomerId || location?.state?.customer?.customerId || pathCustomerId;

  // Use notes context with safe defaults in case context isn't available
  const notesContext = useNotes() || {};
  const {
    notes = [],
    loading = false,
    hasApiError = false,
    hasMore = false,
    searchLoading = false,
    addingNote = false,
  } = notesContext;

  // Use notes actions from context with safe fallbacks
  const {
    loadMoreNotes = () => {
      /* No-op fallback */
    },
    addNewNote = () => {
      /* No-op fallback */
    },
    updateNote = () => {
      /* No-op fallback */
    },
    removeNote = () => {
      /* No-op fallback */
    },
    searchCustomerNotes = () => {
      /* No-op fallback */
    },
  } = useNotesActions() || {};

  useEffect(() => {
    const fetchNotesConfig = async () => {
      try {
        const response = await getStoreConfigs({
          storeNumbers: [getSelectedStore()],
          paramKeyNames: ['EnableCustomerNotes'],
        });
        const notesValue =
          response?.[0]?.configDetails?.find(
            (config) => config.paramKeyName === 'EnableCustomerNotes'
          )?.paramValue || '0';
        setEnableCustomerNotes(notesValue);
      } catch (error) {
        setEnableCustomerNotes('0');
      }
    };
    fetchNotesConfig();
  }, []);

  const handleAddNote = useCallback(
    (noteText: string) => {
      try {
        if (customerId && noteText) {
          addNewNote(customerId, noteText, 'STMT');
        }
      } catch (error) {
        // Error handled by context
      }
    },
    [addNewNote, customerId]
  );

  // Handle searching notes with context
  const handleSearchNotes = useCallback(
    (query: string) => {
      try {
        if (customerId && typeof query === 'string') {
          searchCustomerNotes(customerId, query);
        }
      } catch (error) {
        // Error handled by context
      }
    },
    [searchCustomerNotes, customerId]
  );

  // Handle loading more notes with context
  const handleLoadMoreNotes = useCallback(() => {
    try {
      if (customerId) {
        loadMoreNotes(customerId);
      }
    } catch (error) {
      // Error handled by context
    }
  }, [loadMoreNotes, customerId]);

  // Handle editing notes
  const handleEditNote = useCallback(
    async (payload: {
      customerId: string;
      note: string;
      customerNotesId: string;
    }) => {
      try {
        await updateNote(
          payload.customerId,
          payload.note,
          payload.customerNotesId
        );
      } catch (error) {
        // Error handled by context, but ensure UI can continue
        // eslint-disable-next-line no-console
        console.error('Edit note failed:', error);
      }
    },
    [updateNote]
  );

  // Handle deleting notes
  const handleDeleteNote = useCallback(
    async (customerNotesId: string) => {
      try {
        if (customerId && customerNotesId) {
          await removeNote(customerId, customerNotesId);
        }
      } catch (error) {
        // Error handled by context
        // eslint-disable-next-line no-console
        console.error('Delete note failed:', error);
      }
    },
    [removeNote, customerId]
  );

  return (
    <>
      <RACAccordion
        details={<CustomerInformationDetails />}
        accordionProps={{
          classes: {
            root: classes.customerInformationRoot,
          },
        }}
        summaryProps={{
          classes: {
            root: classes.accordionSummaryRoot,
            content: classes.accordionSummaryContent,
          },
        }}
      />
      {enableCustomerNotes === '1' && (
        <RACNotesDrawer
          notesOpen={notesOpen}
          onOpenNotes={() => setNotesOpen(true)}
          onCloseNotes={() => setNotesOpen(false)}
          triggerPosition={{ right: 0, top: 160 }}
          triggerSize={{ width: 35, height: 91 }}
        >
          <div className={classes.drawerContent}>
            <AddNote onAddNote={handleAddNote} isSubmitting={addingNote} />
            <SearchNotes
              onSearch={handleSearchNotes}
              isSearching={searchLoading}
            />
            <div id="notesListWrapper" className={classes.notesListWrapper}>
              {hasApiError ? (
                <div
                  style={{ padding: '16px', textAlign: 'center', color: 'red' }}
                >
                  Error loading notes. Please try again.
                </div>
              ) : (
                <NotesList
                  notes={notes || []}
                  loading={loading || false}
                  hasMoreNotes={hasMore || false}
                  onLoadMore={handleLoadMoreNotes}
                  onEditNote={handleEditNote}
                  onDeleteNote={handleDeleteNote}
                  customerId={customerId || ''}
                  addingNote={addingNote}
                />
              )}
            </div>
          </div>
        </RACNotesDrawer>
      )}
    </>
  );
};
